import { ApolloError, useLazyQuery } from '@apollo/client';
import { commodityPageSize } from '@bechtel/client-common';
import { GET_COMMODITIES, GET_WORKPACKAGE_DETAIL_BY_IDS } from '@bechtel/client-gql';
import {
  Box,
  Button,
  ButtonAppearance,
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
  useModalState,
} from '@bechtel/gds';
import {
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityList,
  CommoditySortByTerm,
  FilterOperator,
  PagingInput,
  ResolveBlockError,
  SortOrder,
  WorkPackage,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { FC } from 'react';

interface BlockNotResolvedProps {
  workPackageId: WorkPackage['id'];
  resolveBlockError: ResolveBlockError | ApolloError;
  onClose: () => void;
}

const getErrorMessage = (error: BlockNotResolvedProps['resolveBlockError']) => {
  if (error instanceof ApolloError)
    return (
      <Trans>
        Network error has occured.Please press the &ldquo;Retry&rdquo; button to fetch the latest data and try again.
      </Trans>
    );
  switch (error) {
    case ResolveBlockError.RESOLVE_BLOCK_ERRORED:
      return <Trans>The block you are trying to resolve having some error. Please try later. </Trans>;
    case ResolveBlockError.COMMENT_REQUIRED:
      return <Trans>Resolution Notes are mandatory</Trans>;
    case ResolveBlockError.COMMENT_TOO_LARGE:
      return <Trans>Resolution notes exceeded the maximum characters limit</Trans>;
    case ResolveBlockError.NOT_BLOCKED:
      return (
        <Trans>
          The block you are trying to resolve is already resolved. Please press the &ldquo;Update&rdquo; button to fetch
          the latest data and try again.
        </Trans>
      );
    case ResolveBlockError.NOT_FOUND:
      return (
        <Trans>
          The block you are trying to resolve is not found. Please press the &ldquo;Update&rdquo; button to fetch the
          latest data and try again.
        </Trans>
      );
    case ResolveBlockError.MISSING_REQUIRED_PERMISSION:
      return <Trans>You do not have permission to resolve block.</Trans>;
    case ResolveBlockError.SERVICE_PROXY_ERROR:
      return <Trans>Downstream service is down. Please try again after sometime. </Trans>;
    default:
      <Trans>Some error has occured. Please contact Administrator.</Trans>;
  }
};

export const BlockNotResolved: FC<BlockNotResolvedProps> = ({ workPackageId, resolveBlockError, onClose }) => {
  const { visible, close } = useModalState(true);
  const [loadWorkPackagebyId, { loading: workPackageLoading }] = useLazyQuery(GET_WORKPACKAGE_DETAIL_BY_IDS, {
    variables: {
      ids: [workPackageId],
    },
    fetchPolicy: 'network-only',
  });

  const [loadCommodityByWorkPackageId, { loading: commodityLoading }] = useLazyQuery<
    { commodityList: CommodityList },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_COMMODITIES, {
    variables: {
      input: {
        complexFilter: {
          and: [
            { term: CommodityFilterByTerm.WORK_PACKAGE_ID, value: workPackageId, operator: FilterOperator.EQUALS },
            { term: CommodityFilterByTerm.EXCLUDE_CONNECTED_COMMODITY, value: 'true', operator: FilterOperator.EQUALS },
          ],
        },
        sortByFilter: {
          term: CommoditySortByTerm.TAG,
          sortOrder: SortOrder.ASC,
        },
      },
      paging: { cursor: '1', limit: commodityPageSize },
    },
    notifyOnNetworkStatusChange: true,
  });

  const loading = workPackageLoading || commodityLoading;

  return (
    <Modal visible={visible}>
      <ModalHeader title={t`Alert Message`}>
        <ModalClose onClick={onClose} />
      </ModalHeader>
      <ModalBody>
        <Box px={{ sm: '500' }} py={{ sm: '700' }}>
          <Text size="600">{getErrorMessage(resolveBlockError)}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button appearance={ButtonAppearance.SECONDARY} onClick={close}>
              <Trans> Close</Trans>
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={async () => {
                await loadWorkPackagebyId();
                await loadCommodityByWorkPackageId();
                close();
              }}
              disabled={loading}
            >
              {loading && <Spinner></Spinner>}
              {!loading && (resolveBlockError instanceof ApolloError ? t`Retry` : t`Update`)}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
